'use client';

import { Button } from '@/components/ui/button';
import {
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  Sparkles,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';

const SLIDES = ['/img/hero-11.png', '/img/hero-12.png', '/img/hero-13.png', '/img/hero-14.png','/img/hero-15.png','/img/hero-16.png'] as const;

// Timing
const INTERVAL_MS = 6500; // time per slide (includes transition)
const TRANSITION_MS = 1100;

// Effects
type Effect = 'fadeZoom' | 'slide' | 'blurFade' | 'tiltSweep';

export function HeroSection() {
  const slides = useMemo(() => SLIDES, []);
  const total = slides.length;

  const [active, setActive] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);
  const [phase, setPhase] = useState<'idle' | 'from' | 'to'>('idle');
  const [playing, setPlaying] = useState(true);
  const [effect, setEffect] = useState<Effect>('fadeZoom');
  const [controlsOpen, setControlsOpen] = useState(false);

  // Auto-switch effects:
  // - After 1st full cycle => FX2 (slide)
  // - After 2nd full cycle => FX3 (blurFade)
  // - After 3rd full cycle => FX4 (tiltSweep)
  // (Only if user didn't manually change the effect)
  const userTouchedEffect = useRef(false);
  useEffect(() => {
    const t1 = window.setTimeout(() => {
      if (!userTouchedEffect.current) setEffect('slide');
    }, total * INTERVAL_MS + 50);

    const t2 = window.setTimeout(() => {
      if (!userTouchedEffect.current) setEffect('blurFade');
    }, total * INTERVAL_MS * 2 + 100);

    const t3 = window.setTimeout(() => {
      if (!userTouchedEffect.current) setEffect('tiltSweep');
    }, total * INTERVAL_MS * 3 + 150);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [total]);

  const goTo = (nextIndex: number) => {
    const clamped = ((nextIndex % total) + total) % total;
    if (clamped === active) return;

    setPrev(active);
    setActive(clamped);
    setPhase('from');

    // flip to 'to' on next paint so transitions animate
    requestAnimationFrame(() => setPhase('to'));

    window.setTimeout(() => {
      setPhase('idle');
      setPrev(null);
    }, TRANSITION_MS);
  };

  const next = () => goTo(active + 1);
  const previous = () => goTo(active - 1);

  // Autoplay
  useEffect(() => {
    if (!playing) return;
    const id = window.setTimeout(() => next(), INTERVAL_MS);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, playing, effect]);

  const setEffectSafe = (e: Effect) => {
    userTouchedEffect.current = true;
    setEffect(e);
  };

  const cycleEffect = () => {
    userTouchedEffect.current = true;
    setEffect((e) =>
      e === 'fadeZoom' ? 'slide' : e === 'slide' ? 'blurFade' : e === 'blurFade' ? 'tiltSweep' : 'fadeZoom',
    );
  };

  const layerStyle = (role: 'prev' | 'active') => {
    const isTransition = prev !== null && phase !== 'idle';

    // idle: show only active layer with gentle zoom for life
    if (!isTransition) {
      return {
        opacity: role === 'active' ? 1 : 0,
        transform: 'translate3d(0%,0%,0) scale(1.04) rotate(0deg)',
        filter: 'blur(0px)',
      } as const;
    }

    // Defaults
    let opacity = role === 'active' ? 1 : 0;
    let x = 0; // %
    let y = 0; // %
    let scale = 1.02;
    let rotate = 0; // deg
    let blur = 0;

    if (effect === 'fadeZoom') {
      if (phase === 'from') {
        opacity = role === 'active' ? 0 : 1;
        scale = role === 'active' ? 1.08 : 1.02;
      } else {
        opacity = role === 'active' ? 1 : 0;
        scale = role === 'active' ? 1.02 : 1.02;
      }
      x = 0;
      y = 0;
      rotate = 0;
      blur = 0;
    } else if (effect === 'slide') {
      if (phase === 'from') {
        opacity = role === 'active' ? 0 : 1;
        x = role === 'active' ? 10 : 0;
      } else {
        opacity = role === 'active' ? 1 : 0;
        x = role === 'active' ? 0 : -10;
      }
      y = 0;
      scale = 1.02;
      rotate = 0;
      blur = 0;
    } else if (effect === 'blurFade') {
      // FX3
      if (phase === 'from') {
        opacity = role === 'active' ? 0 : 1;
        scale = role === 'active' ? 1.04 : 1.02;
        blur = role === 'active' ? 10 : 0;
      } else {
        opacity = role === 'active' ? 1 : 0;
        scale = role === 'active' ? 1.02 : 1.02;
        blur = role === 'active' ? 0 : 12;
      }
      x = 0;
      y = 0;
      rotate = 0;
    } else {
      // tiltSweep (FX4): subtle rotate + vertical drift + crossfade
      if (phase === 'from') {
        opacity = role === 'active' ? 0 : 1;
        scale = role === 'active' ? 1.06 : 1.02;
        y = role === 'active' ? 6 : 0;
        rotate = role === 'active' ? 1.6 : 0;
      } else {
        opacity = role === 'active' ? 1 : 0;
        scale = role === 'active' ? 1.02 : 1.02;
        y = role === 'active' ? 0 : -6;
        rotate = role === 'active' ? 0 : -1.2;
      }
      x = 0;
      blur = 0;
    }

    return {
      opacity,
      transform: `translate3d(${x}%,${y}%,0) scale(${scale}) rotate(${rotate}deg)`,
      filter: `blur(${blur}px)`,
    } as const;
  };

  const isTransitioning = prev !== null && phase !== 'idle';

  const effectTitle =
    effect === 'fadeZoom'
      ? 'FX1: Fade + Zoom'
      : effect === 'slide'
        ? 'FX2: Slide'
        : effect === 'blurFade'
          ? 'FX3: Blur + Fade'
          : 'FX4: Tilt + Sweep';

  return (
    <section className="relative overflow-hidden text-white">
      {/* Background slideshow (clear images, no full overlay) */}
      <div aria-hidden className="absolute inset-0">
        {/* Previous layer (only visible during transitions) */}
        {prev !== null && (
          <div
            className="absolute inset-0 transition-[opacity,transform,filter] ease-in-out"
            style={{ ...layerStyle('prev'), transitionDuration: `${TRANSITION_MS}ms` }}
          >
            <Image
              src={slides[prev]}
              alt=""
              fill
              priority={false}
              sizes="800vw"
              quality={92}
              className="object-cover select-none"
            />
          </div>
        )}

        {/* Active layer */}
        <div
          className="absolute inset-0 transition-[opacity,transform,filter] ease-in-out"
          style={{ ...layerStyle('active'), transitionDuration: `${TRANSITION_MS}ms` }}
        >
          <Image
            src={slides[active]}
            alt=""
            fill
            priority
            sizes="800vw"
            quality={92}
            className="object-cover select-none"
          />
        </div>

        {/* Subtle vignette ONLY when transitioning (keeps image clear most of the time) */}
        {isTransitioning ? <div className="absolute inset-0 bg-black/10" /> : null}
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-24 md:py-32">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-2 drop-shadow-[0_6px_18px_rgba(0,0,0,0.35)]">
            Always Fresh Plus Natural
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-2xl mx-auto drop-shadow-[0_4px_12px_rgba(0,0,0,0.35)]">
            tropical products &amp; exotic flavours
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
            {/* Fruits: fancy orange/autumn vibe */}
            <Button
              asChild
              variant="outline"
              size="lg"
              className="min-w-[200px]
                         bg-white/10 border-orange-300/50
                         hover:bg-orange-500/10 hover:border-orange-200/80
                         shadow-lg"
            >
              <Link
                href="/search"
                className="font-semibold tracking-wide
                           text-transparent bg-clip-text
                           bg-gradient-to-r from-amber-300 via-orange-400 to-orange-600"
              >
                Fruits
              </Link>
            </Button>

            {/* Veggies: green/vegetable vibe */}
            <Button
              asChild
              variant="outline"
              size="lg"
              className="min-w-[200px]
                         bg-white/10
                         border-emerald-300/40
                         text-emerald-200
                         hover:bg-emerald-500/15
                         hover:text-emerald-50
                         hover:border-emerald-200/70"
            >
              <a href="/search" target="_self" rel="noopener noreferrer">
                Veggies
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Tiny toggle control (bottom-right) */}
      <div className="absolute bottom-5 right-5 z-20">
        {/* Toggle button */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={controlsOpen ? 'Close slideshow controls' : 'Open slideshow controls'}
          onClick={() => setControlsOpen((v) => !v)}
          className="rounded-full border border-white/20 bg-white/10 backdrop-blur-md shadow-lg
                     text-white hover:bg-white/15 hover:scale-105 transition"
        >
          {controlsOpen ? <X className="h-5 w-5" /> : <SlidersHorizontal className="h-5 w-5" />}
        </Button>

        {/* Panel */}
        {controlsOpen ? (
          <div className="mt-2 w-[232px] rounded-2xl border border-white/20 bg-white/10 backdrop-blur-md shadow-xl p-3">
            {/* Dots */}
            <div className="flex items-center justify-center gap-2 pb-3">
              {slides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => goTo(i)}
                  className={[
                    'h-2.5 w-2.5 rounded-full transition-all',
                    i === active ? 'bg-white w-7' : 'bg-white/45 hover:bg-white/70',
                  ].join(' ')}
                />
              ))}
            </div>

            {/* Controls row */}
            <div className="flex items-center justify-between gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Previous slide"
                onClick={previous}
                className="text-white hover:bg-white/15 hover:scale-105 transition"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>

              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={playing ? 'Pause slideshow' : 'Play slideshow'}
                onClick={() => setPlaying((p) => !p)}
                className="text-white hover:bg-white/15 hover:scale-105 transition"
              >
                {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
              </Button>

              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Next slide"
                onClick={next}
                className="text-white hover:bg-white/15 hover:scale-105 transition"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>

              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Change slideshow effect"
                onClick={cycleEffect}
                className="text-white hover:bg-white/15 hover:scale-105 transition"
                title={effectTitle}
              >
                <Sparkles className="h-5 w-5" />
              </Button>
            </div>

            {/* Effect pills */}
            <div className="mt-3 flex items-center justify-center gap-2">
              {([
                ['fadeZoom', 'FX1'] as const,
                ['slide', 'FX2'] as const,
                ['blurFade', 'FX3'] as const,
                ['tiltSweep', 'FX4'] as const,
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setEffectSafe(key)}
                  className={[
                    'text-xs px-2.5 py-1 rounded-full border transition',
                    effect === key
                      ? 'bg-white/20 border-white/30 text-white'
                      : 'bg-transparent border-white/20 text-white/80 hover:bg-white/10',
                  ].join(' ')}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="pt-2 text-center text-[11px] text-white/75 select-none">
              Auto: FX1 → FX2 → FX3 → FX4 (each full cycle)
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

